import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { IPerson } from '@modules/contacts/shared';
import type { CreateOrganizationPersonInput } from '../../shared';

export interface CreateOrgPersonResult {
  person: IPerson;
  inviteUrl: string | null;
}

/**
 * Person mutations within an organization. Reads (the org's person list) use a
 * reactive `httpResource` in the component; this service holds only the create
 * mutation (mutations are kept imperative, per the app's resource-API pattern).
 */
@Injectable({ providedIn: 'root' })
export class OrganizationPersonApiService {
  readonly #http = inject(HttpClient);

  create(
    organizationId: string,
    input: CreateOrganizationPersonInput,
  ): Promise<{ success: boolean; data: CreateOrgPersonResult }> {
    return firstValueFrom(
      this.#http.post<{ success: boolean; data: CreateOrgPersonResult }>(
        `/api/v1/organization/organizations/${organizationId}/persons`,
        input,
      ),
    );
  }

  /** Re-issue and resend the invite email for a pending (unlinked) person. */
  resendInvite(
    organizationId: string,
    personId: string,
  ): Promise<{ success: boolean; data: { inviteUrl: string | null } }> {
    return firstValueFrom(
      this.#http.post<{ success: boolean; data: { inviteUrl: string | null } }>(
        `/api/v1/organization/organizations/${organizationId}/persons/${personId}/resend-invite`,
        {},
      ),
    );
  }

  /** Remove a person from the organization. */
  delete(organizationId: string, personId: string): Promise<{ success: boolean }> {
    return firstValueFrom(
      this.#http.delete<{ success: boolean }>(
        `/api/v1/organization/organizations/${organizationId}/persons/${personId}`,
      ),
    );
  }
}
